import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { generalActions } from "../../store/actions";
import { plannerApi, retryAfterSeconds } from "./api/plannerApi";
import { isRequestCanceled } from "../../helpers/abortableRequest";
import { invalidateResourceInsights } from "../../components/resourceInsights/resourceInsights.api";
import { createPlannerEngine } from "./engine/createPlannerEngine";
import { readShowHours, writeShowHours } from "./showHours";
import { readPanelWidth, writePanelWidth } from "./panelWidth";
import { PlannerEngineContext } from "./plannerContext";

/** How long the tab must have been hidden before coming back to it refreshes the planner. */
const IDLE_REFRESH_MS = 10 * 60 * 1000;

/**
 * Creates the planner engine when the screen is shown and disposes it when the screen goes, which also cancels
 * whatever the engine still has on its way. The first page is requested as soon as the engine exists.
 */
export default function PlannerProvider({ children }) {
  const dispatch = useDispatch();
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  const [engine, setEngine] = useState(null);

  useEffect(() => {
    const created = createPlannerEngine({
      api: plannerApi,
      isCanceled: isRequestCanceled,
      retryAfterSeconds,
      notify: {
        error: (title) => dispatchRef.current(generalActions.showToast({ title, type: "error" })),
        saved: () => invalidateResourceInsights(),
      },
      readShowHours,
      writeShowHours,
      readPanelWidth,
      writePanelWidth,
    });

    created.actions.applyFilters();
    setEngine(created);

    // What is held may be stale after a long time away from the tab, so the resources on screen are refreshed.
    let hiddenAt = null;
    const onVisibilityChange = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
        return;
      }
      if (hiddenAt !== null && Date.now() - hiddenAt > IDLE_REFRESH_MS) created.actions.refresh();
      hiddenAt = null;
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      created.dispose();
    };
  }, []);

  if (!engine) return null;

  return <PlannerEngineContext.Provider value={engine}>{children}</PlannerEngineContext.Provider>;
}
